/**
 * Route Loading Fallback Component
 *
 * Displays a loading spinner and optional message during route chunk loading.
 * Used as Suspense fallback for lazy-loaded route components.
 */

interface RouteLoadingFallbackProps {
  /** Optional loading message to display */
  message?: string
}

export function RouteLoadingFallback({
  message = 'Loading page...',
}: RouteLoadingFallbackProps) {
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="flex flex-col items-center gap-4">
        {/* Spinning loader */}
        <div
          className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 dark:border-gray-700 border-t-blue-600 dark:border-t-blue-500"
          role="status"
          aria-label="Loading"
        />

        {/* Loading message */}
        <p className="text-sm text-gray-600 dark:text-gray-400 animate-pulse">
          {message}
        </p>
      </div>
    </div>
  )
}

/**
 * Global Loading Fallback Component
 *
 * Displays app logo and loading spinner for initial app load.
 * Used at the root level Suspense boundary.
 */
export function GlobalLoadingFallback() {
  return (
    <div className="fixed inset-0 bg-white dark:bg-gray-900 flex items-center justify-center">
      <div className="flex flex-col items-center gap-6">
        {/* App logo with pulse animation */}
        <div className="animate-pulse">
          <div className="h-16 w-16 bg-blue-600 dark:bg-blue-500 rounded-lg flex items-center justify-center">
            <span className="text-white text-2xl font-bold">M</span>
          </div>
        </div>

        {/* Spinning loader */}
        <div
          className="animate-spin rounded-full h-8 w-8 border-4 border-gray-200 dark:border-gray-700 border-t-blue-600 dark:border-t-blue-500"
          role="status"
          aria-label="Loading application"
        />

        <p className="text-sm text-gray-500 dark:text-gray-400">
          Loading application...
        </p>
      </div>
    </div>
  )
}

/**
 * Lightweight Loading Spinner Component
 *
 * Minimal loading indicator for component-level suspense boundaries.
 * Doesn't take up full viewport space.
 */
export function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center p-8">
      <div
        className="animate-spin rounded-full h-8 w-8 border-4 border-gray-200 dark:border-gray-700 border-t-blue-600 dark:border-t-blue-500"
        role="status"
        aria-label="Loading"
      />
    </div>
  )
}
